'use client';

import { Alert, Box, Container, Typography } from '@mui/material';

import LoadingSpinner from '@/components/atoms/LoadingSpinner';
import Breadcrumb from '@/components/molecules/Breadcrumb';
import {
  AboutHeroSection,
  CoreValuesSection,
  CTASection,
  FeaturedProjectsSection,
  PartnersSection,
} from '@/components/organisms';
import { useAboutPage } from '@/hooks/useAboutPage';
import { getImageAlt, getImageUrl } from '@/services/utils';

/**
 * About Page - Company information and values
 * Header and footer are provided by ClientLayoutWrapper in root layout
 */
export default function AboutPage() {
  const { data, loading, error, refetch } = useAboutPage();

  const breadcrumbItems = [
    { label: 'Trang chủ', href: '/' },
    { label: 'Giới thiệu', href: '/about' },
  ];

  // Show loading state while fetching data
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <LoadingSpinner message='Đang tải trang...' size={60} />
      </Box>
    );
  }

  // Show error state if data fetching failed
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Alert
          severity='error'
          sx={{ maxWidth: 600 }}
          action={
            <button
              onClick={refetch}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
              }}
            >
              Thử lại
            </button>
          }
        >
          <Typography variant='h6' gutterBottom>
            Lỗi khi tải dữ liệu
          </Typography>
          <Typography variant='body2'>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  // Safe data access with fallbacks
  const heroData = data?.hero;
  const coreValuesData = data?.coreValues || undefined;
  const featuredProjectsData = data?.featuredProjects || undefined;
  const partnersData = data?.partners || undefined;
  const ctaData = data?.cta || undefined;

  return (
    <>
      {/* Breadcrumb */}
      <Container maxWidth='lg' sx={{ py: 2 }}>
        <Breadcrumb items={breadcrumbItems} />
      </Container>

      {/* Hero Section */}
      <AboutHeroSection
        heroData={heroData}
        getImageUrl={getImageUrl}
        getImageAlt={getImageAlt}
      />

      {/* Core Values Section */}
      <CoreValuesSection data={coreValuesData} />

      {/* Partners Section */}
      <PartnersSection data={partnersData} />

      {/* Featured Projects Section */}
      <FeaturedProjectsSection data={featuredProjectsData} />

      {/* CTA Section */}
      <CTASection data={ctaData} />
    </>
  );
}
